import { cloneDeep } from 'lodash';
import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../../services/api';
import { session } from '../../../services/session';

import { getClass } from '../../../utils/component-helpers';
import { useOutsideClick } from '../../../utils/custom-hooks';
import { Loader, NoResult } from '../../misc/loader-utils';
import './strategy-typeahead.scss';
import './strategy-cogs.scss';

let timeQuery = null;
let defaultPublishers = [];
const publishersDict = {};
const optionHeight = 40;
const maxHeight = 390;
const tabsHeight = 47;

const pluralTypes = {
  category: 'categories',
  brand: 'brands',
  publisher: 'publishers',
};

function StrategyTypeahead({ type, triggerProp, updateChips, initialPubs, cogs, updateStrategy }) {
  /* State */
  const [options, setOptions] = useState([]);
  const [textInput, setInput] = useState('');
  const [showCogs, setShowCogs] = useState(true);
  const [optionsLoading, setLoading] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [tab, setTab] = useState('all');
  const inputNode = useRef(null);
  const typeNode = useRef(null);

  /* Constants */
  const endpoint = `/api/v0/merchants/${session.orgId}/strategy/type_ahead/`;
  const pluralType = pluralTypes[type];
  const triggerKey = type === 'publisher' ? 'pub_id' : 'term';
  const displayKey = type === 'publisher' ? 'full_name' : 'term';
  // Remove once backend supports pub triggers
  const triggers = triggerProp || [];

  /* Effects */
  useOutsideClick(inputNode, setShowOptions, () => {
    // Sets default publishers on initial load
    if (type === 'publisher') {
      setOptions(initialPubs);
      defaultPublishers = initialPubs;
      defaultPublishers.forEach(pub => {
        publishersDict[`${pub.pub_id}`] = pub.full_name;
      });
    }
  });

  useEffect(() => {
    const newOptions = cloneDeep(options).map(option => {
      option.selected = triggers.includes(option[triggerKey]);
      return option;
    });
    if (newOptions.length) {
      setOptions(newOptions);
    }
  }, [triggerProp]);

  /* Methods */
  function updateInput(val) {
    if (timeQuery) clearTimeout(timeQuery);

    if (val.length >= 3) {
      timeQuery = setTimeout(() => {
        queryTypeahead(val);
      }, 500);
      setLoading(true);
    }

    setInput(val);
    setShowOptions(!!val);

    if (!val && type === 'publisher') {
      setOptions(defaultPublishers || []);
    } else {
      setOptions([]);
    }
  }

  function queryTypeahead(search) {
    api.get(endpoint, { search_query: search, type }).then(resp => {
      const { stats } = resp.data.data[0];
      const newOptions = stats.map(item => {
        if (triggers.includes(item[triggerKey])) {
          item.selected = true;
        }
        return item;
      });
      setOptions(newOptions);
      setLoading(false);
    });
  }

  function inputOnClick() {
    if (type === 'publisher' || (!showOptions && textInput)) {
      setShowOptions(true);
    }
  }

  function handleKeyInputs(e) {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      if (!textInput && triggers.length) {
        updateChips(pluralType, null, triggers[triggers.length - 1]);
      }
    }
  }

  function selectAll(allSelected) {
    const chips = cloneDeep(options).map(option => option[triggerKey]);
    if (allSelected) {
      updateChips(pluralType, null, chips);
    } else {
      updateChips(pluralType, chips);
    }
  }

  function selectOption(selected) {
    if (selected.selected) {
      updateChips(pluralType, null, selected[triggerKey]);
    } else {
      updateChips(pluralType, [selected[triggerKey]]);
    }
  }

  function updateCogs(val) {
    let cogs = val;
    if (parseFloat(val) < 1) cogs = `${parseFloat(val) * 10}`;
    if (parseFloat(val) > 100) cogs = '100';
    updateStrategy({ cogs });
  }

  /* Render */
  let chips = triggers.map((trigger, i) => {
    const triggerName = type === 'publisher' ? publishersDict[trigger] : trigger;
    if (i <= 9) {
      return (
        <div key={`${triggerName}-chip-${i}`} className={`chip-container ${type}`}>
          <div className="chip-label">{triggerName}</div>
          <div className="delete-chip" onClick={() => updateChips(pluralType, null, trigger)} />
        </div>
      );
    }
    if (i === 10) {
      return (
        <div
          key={`${triggerName}-chip-${i}`}
          className={`chip-container ${type}`}
          onClick={() => {
            setOptions(
              triggers.map(trigger => {
                return { term: trigger, selected: true };
              }),
            );
          }}
        >
          <div className="chip-label">+{triggers.length - i}</div>
        </div>
      );
    }
    return null;
  });

  // Publisher typeahead has tabbed filtering of options
  const selectedOptions = options.filter(option => option.selected);
  const excludedOptions = options.filter(option => !option.selected);
  const filteredOptions = tab === 'selected' ? selectedOptions : excludedOptions;
  const renderedOptions =
    type === 'publisher' && ['selected', 'excluded'].includes(tab) ? filteredOptions : options;

  const tabArr = [
    {
      name: 'all',
      text: `All ${textInput ? 'Results' : 'Publishers'} (${options.length})`,
    },
    {
      name: 'selected',
      text: `Selected (${selectedOptions.length})`,
    },
    {
      name: 'excluded',
      text: `Excluded (${excludedOptions.length})`,
    },
  ];

  const tabs =
    type === 'publisher' && !optionsLoading && options.length ? (
      <div className="tabs">
        {tabArr.map((data, i) => {
          return (
            <div
              key={`tab-${i}`}
              onClick={e => setTab(data.name)}
              className={`tab ${data.name} ${tab === data.name ? 'current' : ''}`}
            >
              {data.text}
            </div>
          );
        })}
      </div>
    ) : null;

  if (triggers.length > 10 && type === 'publisher') {
    chips = (
      <>
        <div className={`chip-container ${type}`}>
          <div className="chip-label">Selected ({triggers.length})</div>
        </div>
        <div className={`chip-container excluded ${type}`}>
          <div className="chip-label">Excluded ({defaultPublishers.length - triggers.length})</div>
        </div>
      </>
    );
  }

  const optionValueNodes =
    !optionsLoading && options.length
      ? renderedOptions.map((option, i) => {
          return (
            <div
              key={`option-${i}`}
              className={getClass('option', {
                selected: option.selected,
              })}
              onClick={() => selectOption(option)}
            >
              <span className="checkbox"></span>
              <span className="value">{option[displayKey]}</span>
            </div>
          );
        })
      : null;

  const allSelected = renderedOptions.every(item => item.selected);
  const selectAllNode =
    !optionsLoading && options.length ? (
      <div className="select-all" onClick={() => selectAll(allSelected)}>
        {allSelected ? 'Deselect' : 'Select'} All ({renderedOptions.length})
      </div>
    ) : null;

  // containerHeight var passed to css file to ensure typeahead position
  const additional = type === 'publisher' ? tabsHeight : 0;
  const containerHeight = renderedOptions.length
    ? (renderedOptions.length + 1) * optionHeight + additional > maxHeight
      ? maxHeight + 10
      : (renderedOptions.length + 1) * optionHeight + additional + 10
    : options.length
    ? 97
    : 60;

  const typeAhead = showOptions ? (
    <div
      ref={node => (typeNode.current = node)}
      className="typeahead-container"
      style={{ '--search-height': `-${containerHeight}px` }}
    >
      {tabs}
      {selectAllNode}
      {optionValueNodes}
      <Loader loading={optionsLoading} />
      <NoResult noResult={!options.length && !optionsLoading} type="strategy-typeahead" />
    </div>
  ) : null;

  const searchIcon = !triggers.length ? <div className="narrativ-icon search" /> : null;

  const cogsRows =
    type === 'publisher' && defaultPublishers.length
      ? triggers.map((trigger, i) => {
          const pub = defaultPublishers.find(pub => pub.pub_id === trigger) || {};
          return (
            <div key={`cogs-row-${i}`} className="cogs-row">
              <div className="name">{pub.full_name}</div>
              <div className="default-cogs">{parseFloat(pub.cogs || 0) * 100}%</div>
              <div className="new-cogs">
                {cogs}
                {cogs ? '%' : ''}
              </div>
            </div>
          );
        })
      : null;

  const cogsTable =
    type === 'publisher' && showCogs ? (
      <div className="cogs-table">
        <div className="header-row">
          <div className="name">Publisher</div>
          <div className="default-cogs">Default COGS</div>
          <div className="new-cogs">New COGS</div>
        </div>
        {cogsRows}
      </div>
    ) : null;

  const cogsElement =
    type === 'publisher' && triggers.length ? <div className="cogs">{cogsTable}</div> : null;

  return (
    <>
      <div className={`${type}-typeahead`}>
        <div className="label">{type}</div>
        <div ref={node => (inputNode.current = node)} className="trigger-input">
          {searchIcon}
          {chips}
          <input
            className="input"
            value={textInput}
            placeholder={`Enter ${type} string`}
            spellCheck={false}
            onChange={e => updateInput(e.target.value)}
            onClick={inputOnClick}
            onKeyDown={handleKeyInputs}
          />
          {typeAhead}
        </div>
      </div>
      {cogsElement}
    </>
  );
}

export default StrategyTypeahead;
